import { isoDay } from "./format";

function csvEscape(value) {
  const str = String(value ?? "");
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function csvRow(fields) {
  return fields.map(csvEscape).join(",");
}

/**
 * Rows as a finished CSV document, the last line terminated like the rest.
 *
 * Python's `csv.writer` terminates every row it writes, so the payout sheet
 * needs the trailing break too or it differs from the backend's by two bytes —
 * which is the whole difference between "identical" and "nearly identical"
 * when the two are being diffed to settle a vendor query.
 */
function csvDocument(lines) {
  return `${lines.join("\r\n")}\r\n`;
}

/** The `Range: … to …` line every export opens with. */
function rangeLine({ start, end }) {
  return csvRow([`Range: ${isoDay(start)} to ${isoDay(end)}`]);
}

// ── The payout sheet ────────────────────────────────────────────────────────
//
// This is the same document the backend exporter writes
// (backend/shop/admin/payouts/export.py), and the two are meant to be byte
// for byte identical given the same figures: same report-level facts, same
// columns, totals on row 8, headings on row 10, units from row 11. A payout
// note cites those row numbers, and a vendor query gets settled by reading a
// sheet from one against a sheet from the other, so **any change here needs
// the same change there**, and vice versa. The two test suites assert the
// same promised rows.
//
// Amounts are GBP throughout, because that is how royalties are reckoned
// whatever currency the customer checked out in. The on-screen currency
// toggle deliberately does not reach this sheet: a column of figures
// converted at a hand-typed rate is the kind of number that cannot be checked
// back against anything, and converting one payout total afterwards is easy.
// The per-row `Order checkout currency` still says what the customer paid in.
const PAYOUT_TOTAL_ROW = 8;

/**
 * The royalty rate as the backend spells it.
 *
 * It is a Python float there, so `str()` always shows a decimal point — `15.0`,
 * not `15` — while JSON hands JS a plain number that stringifies to `15`. A
 * whole number is padded to one decimal to match; anything already fractional
 * stringifies the same in both languages.
 */
function royaltyPercent(percent) {
  const value = Number(percent);
  return Number.isInteger(value) ? value.toFixed(1) : String(value);
}

/** The `Royalty: …` line: the formula that produced these numbers, in words. */
function royaltyLine(summary) {
  const basis = summary.basePrice === "paid" ? "price paid" : "list price";
  const framing = summary.deductFrame ? "less framing" : "framing included";
  return `Royalty: ${royaltyPercent(summary.royaltyPercent)}% of ${basis}, ${framing}`;
}

/**
 * The `Generated: …` line, in UTC.
 *
 * Seconds precision with an explicit `+00:00`, which is the spelling Python's
 * `datetime.now(timezone.utc).isoformat(timespec="seconds")` produces, so the
 * two exporters stamp a sheet identically. UTC rather than the exporting
 * browser's clock: a sheet downloaded in London reads an hour out through BST,
 * and two sheets either side of a clock change would not be comparable.
 */
function generatedLine() {
  return `Generated: ${new Date().toISOString().replace(/\.\d+Z$/, "+00:00")}`;
}

const PAYOUT_HEADER = [
  "Timestamp",
  "Order ID",
  "Country",
  "Order checkout currency",
  "Product",
  "Variant",
  // Left to right as the arithmetic runs: list price less the discount is
  // what was paid. Both are on every row, then the one the contract actually
  // used, so a line can be re-checked against either.
  "RRP (GBP)",
  "Discount (GBP)",
  "Paid (GBP)",
  "Base (GBP)",
  "VAT (GBP)",
  "Frame (GBP)",
  // "derived" from the product's own framed-vs-unframed prices, "clamped"
  // where those prices have moved since the sale, "size-median" where no
  // delta could be derived from the variant itself, "not-deducted" where the
  // contract leaves framing in. Exported so a disputed line can be traced.
  "Frame basis",
  "Net Revenue (GBP)",
  "Payout (GBP)",
];

/** A money figure, two decimals, as both exporters write it. */
function money(amount) {
  return Number(amount).toFixed(2);
}

/**
 * The totals, each in the column of the values it sums, so checking one is
 * `=SUM(I11:I871)` against `I8` rather than a hunt through a
 * differently-shaped header block.
 *
 * Positioned by looking each column up in `PAYOUT_HEADER` rather than written
 * out as a short row, so a column that carries no total is explicitly blank
 * and the two can never drift out of alignment.
 */
function payoutTotalRow({ summary, orders }) {
  const sumOf = (field) => orders.reduce((total, o) => total + (o[field] || 0), 0);
  const row = PAYOUT_HEADER.map(() => "");

  row[0] = `TOTAL (${summary.unitsSold.toLocaleString()} units)`;
  const totals = {
    "RRP (GBP)": money(sumOf("rrpGbp")),
    "Discount (GBP)": money(sumOf("discountGbp")),
    "Paid (GBP)": money(sumOf("paidGbp")),
    "Base (GBP)": money(summary.grossRevenue),
    "VAT (GBP)": money(summary.totalVat),
    "Frame (GBP)": money(summary.totalFraming),
    // A count, not a sum, but it belongs to the column it describes.
    "Frame basis": `${(summary.framingEstimatedUnits || 0).toLocaleString()} estimated`,
    "Net Revenue (GBP)": money(summary.totalNetRevenue),
    "Payout (GBP)": money(summary.totalPayout),
  };

  Object.entries(totals).forEach(([column, value]) => {
    row[PAYOUT_HEADER.indexOf(column)] = value;
  });
  return row;
}

/**
 * Build a CSV string for one vendor's payout breakdown, in the layout the
 * backend exporter writes:
 *
 *     Vendor / Range / Royalty / Generated             <- rows 1-4
 *     <blank>                                          <- rows 5-7
 *     TOTAL (861 units),,,,,, 127163.18, 869.27, ...   <- row 8
 *     <blank>                                          <- row 9
 *     Timestamp, Order ID, ..., RRP, Discount, Paid    <- row 10
 *     <one row per unit>                               <- row 11 onwards
 *
 * The totals go above the headings so they are the first thing read, and the
 * headings then sit directly above the rows they name. It also keeps the total
 * outside any range a spreadsheet infers from the data and outside any sort of
 * it — a totals row immediately beneath the headings gets caught by both — and
 * the blank row below it keeps the total out of the table block as well.
 *
 * The contract goes in the header rather than being left implicit: a payout
 * figure with no basis attached is the thing a vendor dispute cannot be
 * settled from, and six months on nobody remembers whether a given sheet was
 * reckoned on list price or on what customers actually paid.
 */
export function buildPayoutsCsv({ vendorName, range, summary, orders }) {
  const lines = [];

  // Report-level facts, which are not sums of anything and so have no column
  // to sit in.
  lines.push(csvRow([`Vendor: ${vendorName}`]));
  lines.push(rangeLine(range));
  lines.push(csvRow([royaltyLine(summary)]));
  lines.push(csvRow([generatedLine()]));

  // Whitespace between the report-level facts and the table, so the two read
  // as separate blocks rather than one run of rows. Padded to length rather
  // than a fixed count of blanks, so the totals row stays on row 8 even if a
  // report-level fact is added or dropped above it.
  const gap = Math.max(0, PAYOUT_TOTAL_ROW - 1 - lines.length);
  lines.push(...Array(gap).fill(""));

  lines.push(csvRow(payoutTotalRow({ summary, orders })));
  lines.push("");
  lines.push(csvRow(PAYOUT_HEADER));

  for (const o of orders) {
    lines.push(
      csvRow([
        o.timestamp,
        o.orderId,
        o.country,
        o.currency,
        o.product,
        o.variant,
        money(o.rrpGbp),
        money(o.discountGbp),
        money(o.paidGbp),
        money(o.base),
        money(o.vat),
        money(o.frame),
        o.frameSource ?? "",
        money(o.netRevenue),
        money(o.payout),
      ])
    );
  }

  return csvDocument(lines);
}

/**
 * Trigger a browser download of `content` as a file — no backend round
 * trip needed since the data's already loaded client-side.
 */
export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
/**
 * Flatten the weekly performance table to CSV. Rates are written as decimals
 * rather than pre-formatted percentages so they stay usable in a spreadsheet,
 * and undefined rates are left blank rather than written as zero.
 */
export function buildWeeklyPerformanceCsv({ range, weeks }) {
  const num = (v, places = 2) =>
    v === null || v === undefined ? "" : Number(v).toFixed(places);

  const lines = [];
  lines.push(rangeLine(range));
  lines.push("");
  lines.push(
    csvRow([
      "Week commencing",
      "Partial",
      "Sessions",
      "Paid share",
      "ATC rate",
      "ATC to checkout",
      "Checkout completion",
      "CVR",
      "Orders",
      "AOV excl shipping (GBP)",
      "Revenue incl shipping (GBP)",
      "Ad spend (GBP)",
      "Clicks",
      "Meta purchases",
      "CPC (GBP)",
      "CPA (GBP)",
      "MER",
      "Top product",
      "Top product share",
      "2nd product",
      "2nd product share",
      "3rd product",
      "3rd product share",
    ])
  );

  for (const w of weeks) {
    lines.push(
      csvRow([
        w.period,
        w.partial ? "Yes" : "No",
        w.sessions,
        num(w.paidShare, 4),
        num(w.atcRate, 4),
        num(w.atcToCheckout, 4),
        num(w.checkoutToPurchase, 4),
        num(w.cvr, 4),
        w.orders,
        num(w.aov),
        num(w.revenue),
        num(w.spend),
        w.clicks,
        w.metaPurchases,
        num(w.cpc),
        num(w.cpa),
        num(w.mer),
        ...[0, 1, 2].flatMap((i) => {
          const product = (w.products || [])[i];
          return product ? [product.title, num(product.share, 4)] : ["", ""];
        }),
      ])
    );
  }

  return lines.join("\r\n");
}

/**
 * Flatten the product table to CSV, one line per product per period so the
 * detail behind every collapsed row survives the export. Rates go out as
 * decimals rather than pre-formatted percentages so they stay usable in a
 * spreadsheet.
 */
export function buildProductPerformanceCsv({ range, rows }) {
  const num = (v, places = 2) =>
    v === null || v === undefined ? "" : Number(v).toFixed(places);

  const lines = [rangeLine(range), ""];
  lines.push(
    csvRow([
      "Product",
      "Type",
      "Period",
      "Ad spend (GBP)",
      "Cost per paid visit (GBP)",
      "CPA (GBP)",
      "MER",
      "Sessions",
      "Paid sessions",
      "Paid share",
      "ATC rate",
      "Page CVR",
      "Product CVR",
      "Orders",
      "Units",
      "AOV (GBP)",
      "Revenue (GBP)",
    ])
  );

  for (const r of rows) {
    lines.push(
      csvRow([
        r.label,
        r.kind,
        r.period || "",
        num(r.spend),
        num(r.costPerSession),
        num(r.cpa),
        num(r.mer, 4),
        r.sessions,
        r.paidSessions,
        num(r.paidShare, 4),
        num(r.atcRate, 4),
        num(r.pageCvr, 4),
        num(r.productCvr, 4),
        r.orders,
        r.units,
        num(r.aov),
        num(r.revenue),
      ])
    );
  }

  return lines.join("\r\n");
}
